/**
 * @module registration/creative/components/id_capture
 */

import React, { Component, PropTypes } from 'react';
import AnimatedButton from 'common/components/animated_button';
import PhotoCapture from 'common/components/photo_capture';
import IdUploaded from 'registration/creative/containers/id_uploaded';
import { LoadingIndicator } from 'common/components/preloader';
import { MOBILE_BREAKPOINT_PX } from 'common/variables';
import { trackGoogle } from 'common/tracking';

/**
 * ID and selfie pictures upload.
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
class IdCapture extends Component {
  constructor(props) {
    super(props)
    this.state = {
      /* on mobile, ID and selfie uploads are handled in different screens */
      mobileStep: window.innerWidth < MOBILE_BREAKPOINT_PX? 1 : null
    }
    this.handleNext = this.handleNext.bind(this)
    this.renderIdCapture = this.renderIdCapture.bind(this)
    this.renderSelfieCapture = this.renderSelfieCapture.bind(this)
  }

  componentDidMount() {
    trackGoogle("registration", "verified phone", "creative");
    this.props.fetchIdPhotoIfNeeded()
  }

  handleNext() {
    /* handle next button according to state and environment */
    const { mobileStep } = this.state
    const { uploadIdPhoto, idUploaded, onComplete, reenableIdPhotoUpload } = this.props

    /* process completed */
    if (idUploaded) {
      onComplete()
      /* allow ID photo and selfie to be uploaded again in case user gets back to it
         during review */
      reenableIdPhotoUpload()
    /* first mobile step: just move to selfie upload */
    } else if (mobileStep == 1) {
      this.setState({mobileStep: 2})
    /* second mobile step or unified desktop step */
    } else {
      uploadIdPhoto()
    }
  }

  renderIdCapture() {
    const { updateIdPhotoLocal, idPhoto, idPhotoApproved } = this.props;

    return (
      <div className="columns small-16 medium-8 large-7 photo-id">
        <h4>
          <span className="show-for-small-only">ID Verification Step 1: </span>Government ID
        </h4>

        <div className="instructions">
          <p>
            Use your <span className="hide-for-large">camera</span>
            <span className="show-for-large">webcam</span> to capture a photo of your
            government-issued ID, <strong>making sure your photo is visible and all text
            is fully readable</strong>. We will not accept an ID that we cannot read.
          </p>
          <p>
            Accepted forms of ID: driver's license, identity card, passport.
          </p>
        </div>

        { idPhotoApproved &&
        <div className="status text-success">
          <i className="fa fa-check-circle" /> Verified
        </div> }

        { idPhotoApproved === false &&
        <div className="status text-danger">
          <i className="fa fa-exclamation-triangle" /> Failed
        </div> }

        <PhotoCapture onChange={(p) => updateIdPhotoLocal(p)} photoLabel="your ID"
          initialValue={idPhoto} readonly={idPhotoApproved}
          placeholder="https://s3.amazonaws.com/crowdspring3-assets/marketing/sample-id-verification.jpg?versionId=iuLAoVoCAihd11An2Hl1GLh7bFXg9ATp"/>
      </div>
    );
  }

  renderSelfieCapture() {
    const { updateIdPhotoLocal, selfie, selfieApproved } = this.props;

    return (
      <div className="columns small-16 medium-8 large-7 selfie">
        <h4>
          <span className="show-for-small-only">ID Verification Step 2: </span>Selfie
        </h4>

        <div className="instructions">
          <p>
            Use your <span className="hide-for-large">camera</span>
            <span className="show-for-large">webcam</span> to capture a brand new photo
            of your face. <strong>Make sure you are facing the camera and your full face
            is clearly visible.</strong> This photo must be taken live - you may not use
            a previously taken photo.
          </p>
        </div>

        { selfieApproved &&
        <div className="status text-success">
          <i className="fa fa-check-circle" /> Verified
        </div> }

        { selfieApproved === false &&
        <div className="status text-danger">
          <i className="fa fa-exclamation-triangle" /> Failed
        </div> }

        <PhotoCapture cropperRatio={1} onChange={(p) => updateIdPhotoLocal(undefined, p)}
          readonly={selfieApproved}
          photoLabel="your selfie" initialValue={selfie} placeholder="https://s3.amazonaws.com/crowdspring3-assets/marketing/selfie-verification.jpg"/>
      </div>)
  }

  render() {
    const { mobileStep } = this.state;
    const {
      idUploaded, idPhoto, selfie, uploadingId, error, magicLinkSentFromMobile,
      fetching, idValidationRejectionNote
    } = this.props;

    /* existing ID photo and selfie being fetched */
    if (fetching) {
      return <LoadingIndicator fullScreen={false}/>
    }

    let btnDisabled = true
    /* ID uploaded */
    if (idUploaded) {
      btnDisabled = false
    /* first mobile step */
    } else if (mobileStep == 1) {
      btnDisabled = !idPhoto
    /* second mobile step */
    } else if (mobileStep == 2) {
      btnDisabled = !selfie
    /* desktop unified step */
    } else {
      btnDisabled = !(idPhoto && selfie)
    }

    /* ID photo and selfie successfully captured */
    if (idUploaded) {
      return <IdUploaded idPhoto={idPhoto} selfie={selfie} onNext={this.handleNext}
                magicLinkSentFromMobile={magicLinkSentFromMobile}/>
    }

    return (
      <div className="small-16 columns capture">
        {/* as submission only occurs on the second step for mobile, allow user to get back
            to step 1 */}
        {mobileStep != 2? null:
        <button className="btn btn-text btn-back" onClick={() => this.setState({mobileStep: 1})}>
          <i className="fa fa-angle-left"></i> Back
        </button>}

        <h2 className="hide-for-small-only">Submit Government ID and Selfie</h2>

        <div className="row align-center">
          {!mobileStep || mobileStep == 1? this.renderIdCapture() : null}

          {!mobileStep || mobileStep == 2? this.renderSelfieCapture() : null}
        </div>

        { !!idValidationRejectionNote &&
        <div className="row">
          <div className="columns large-offset-1">
            <div className="text-danger rejection-note">
              <strong>Note from crowdspring:</strong> { idValidationRejectionNote }
            </div>
          </div>
        </div> }

        <div className="text-left row align-center">
          <div className="columns small-16 large-14">
            <AnimatedButton processing={uploadingId} baseBtnClass="btn btn-tertiary btn-block"
              label="Next" processingLabel="Uploading..." errorMsg={error} successMsg="Done!"
              onClick={this.handleNext} disabled={btnDisabled}/>
          </div>
        </div>
      </div>)
  }
}

IdCapture.propTypes = {
  fetching: PropTypes.bool,
  idPhoto: PropTypes.string,
  selfie: PropTypes.string,
  idUploaded: PropTypes.bool,
  uploadingId: PropTypes.bool,
  magicLinkSentFromMobile: PropTypes.bool,
  error: PropTypes.string,
  uploadIdPhoto: PropTypes.func,
  updateIdPhotoLocal: PropTypes.func,
  onComplete: PropTypes.func,
  reenableIdPhotoUpload: PropTypes.func,
  fetchIdPhotoIfNeeded: PropTypes.func,
  idPhotoApproved: PropTypes.bool,
  selfieApproved: PropTypes.bool,
  idValidationRejectionNote: PropTypes.string,
};

export default IdCapture;




// WEBPACK FOOTER //
// ./registration/creative/components/id_capture.jsx